import { useState } from "react";
import { useTheme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useAiGreetStats } from "../../../components/hooks/useAiGreetStats";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSCircularProgress from "views/system-design/DSCircularProgress";

function AiGreetAnalyticsWidget() {
  const theme = useTheme();
  const [days, setDays] = useState(30);
  const [inputValue, setInputValue] = useState("30");
  const { data: stats, isLoading } = useAiGreetStats(days);

  const hasData = !!stats && stats.total > 0;

  return (
    <DSCard sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 1,
        }}
      >
        <DSTypography variant="h6">AI Greet Generations</DSTypography>
        <DSStack direction="row" alignItems="center" spacing={1}>
          <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
            Last
          </DSTypography>
          <DSTextField
            type="number"
            size="small"
            inputProps={{ min: 1 }}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={() => {
              const parsed = parseInt(inputValue, 10);
              if (!isNaN(parsed) && parsed >= 1) {
                setDays(parsed);
              } else {
                setInputValue(String(days));
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                (e.target as HTMLInputElement).blur();
              }
            }}
            sx={{ width: 80 }}
          />
          <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
            days
          </DSTypography>
        </DSStack>
      </DSBox>
      <DSCardContent sx={{ flex: 1 }}>
        {isLoading ? (
          <DSBox sx={{ textAlign: "center", p: "20px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : hasData ? (
          <>
            <DSBox sx={{ textAlign: "center", mb: "12px" }}>
              <DSTypography sx={{ fontSize: 32, fontWeight: 600 }}>
                {stats.total.toLocaleString()}
              </DSTypography>
              <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
                greetings generated
              </DSTypography>
            </DSBox>

            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.daily} margin={{ left: 0, bottom: 20 }}>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  stroke={theme.palette.white[24]}
                  angle={-20}
                  textAnchor="end"
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  stroke={theme.palette.white[24]}
                  allowDecimals={false}
                  width={35}
                />
                <Tooltip
                  formatter={(v) => [v, "greetings"]}
                  contentStyle={{
                    backgroundColor: theme.palette.paper.elevation8,
                    border: "none",
                    borderRadius: 8,
                  }}
                  cursor={{ fill: theme.palette.white[12] }}
                />
                <Bar dataKey="count" fill="#9C27B0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            {/* Per-tone breakdown */}
            <DSBox sx={{ mt: "12px" }}>
              {stats.byTone.map(({ tone, count }) => (
                <DSBox
                  key={tone}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: "6px",
                    fontSize: 13,
                  }}
                >
                  <span>{tone}</span>
                  <DSBox sx={{ color: "text.secondary" }}>{count}</DSBox>
                </DSBox>
              ))}
            </DSBox>
          </>
        ) : (
          <DSBox
            sx={{ textAlign: "center", p: "20px", color: "text.secondary" }}
          >
            No data available
          </DSBox>
        )}
      </DSCardContent>
      <DSBox
        sx={{ borderTop: `1px solid ${theme.palette.divider}`, px: 2, py: 1.5 }}
      >
        <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
          Total AI greeting generations · broken down by day and tone
        </DSTypography>
      </DSBox>
    </DSCard>
  );
}

export default AiGreetAnalyticsWidget;
